import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, CheckCircle, Info, Cpu, Cog, Zap, Activity, CircuitBoard, Gauge, Thermometer, Fuel, Wind, Droplets, Timer, Percent, Waves, Battery } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Map DTC code prefixes to affected systems
const DTC_SYSTEM_MAP = {
  'P0': { name: 'Fuel & Air Metering', icon: Activity, color: 'text-orange-500' },
  'P1': { name: 'Fuel & Air Metering (Mfr)', icon: Activity, color: 'text-orange-500' },
  'P2': { name: 'Fuel & Air Metering', icon: Activity, color: 'text-orange-500' },
  'P03': { name: 'Ignition System', icon: Zap, color: 'text-yellow-500' },
  'P04': { name: 'Emission Controls', icon: Activity, color: 'text-green-600' },
  'P05': { name: 'Vehicle Speed & Idle', icon: Cog, color: 'text-blue-500' },
  'P06': { name: 'ECU & Outputs', icon: Cpu, color: 'text-purple-500' },
  'P07': { name: 'Transmission', icon: Cog, color: 'text-red-500' },
  'P08': { name: 'Transmission', icon: Cog, color: 'text-red-500' },
  'B': { name: 'Body Systems', icon: CircuitBoard, color: 'text-slate-500' },
  'C': { name: 'Chassis/ABS', icon: Cog, color: 'text-amber-600' },
  'U': { name: 'Network/Communication', icon: CircuitBoard, color: 'text-indigo-500' },
};

// Identify critical codes that impact engine/transmission
const CRITICAL_ENGINE_CODES = ['P0300', 'P0301', 'P0302', 'P0303', 'P0304', 'P0305', 'P0306', 'P0307', 'P0308', 'P0171', 'P0172', 'P0174', 'P0175', 'P0420', 'P0430', 'P0128', 'P0507'];
const CRITICAL_TRANSMISSION_CODES = ['P0700', 'P0715', 'P0720', 'P0730', 'P0740', 'P0750', 'P0755', 'P0760', 'P0765', 'P0770', 'P0775', 'P0780'];

// Comprehensive sensor mapping with keyword matching
// Keys are normalized (lowercase) for flexible matching
const SENSOR_DEFINITIONS = {
  // RPM - Engine Speed
  rpm: { icon: Gauge, color: 'text-blue-500', bgColor: 'bg-blue-50', impact: 'Engine speed affects vibration & sound frequency' },
  'engine rpm': { icon: Gauge, color: 'text-blue-500', bgColor: 'bg-blue-50', impact: 'Engine speed affects vibration & sound frequency' },
  'engine speed': { icon: Gauge, color: 'text-blue-500', bgColor: 'bg-blue-50', impact: 'Engine speed affects vibration & sound frequency' },
  
  // Coolant Temperature
  coolant: { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  ect: { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  'coolant temp': { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  'coolant temperature': { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  'engine coolant': { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  'engine coolant temp': { icon: Thermometer, color: 'text-red-500', bgColor: 'bg-red-50', impact: 'Cooling system health - affects engine knock' },
  
  // Engine Load
  load: { icon: Activity, color: 'text-amber-500', bgColor: 'bg-amber-50', impact: 'Engine stress level - higher = louder operation' },
  'engine load': { icon: Activity, color: 'text-amber-500', bgColor: 'bg-amber-50', impact: 'Engine stress level - higher = louder operation' },
  'calculated load': { icon: Activity, color: 'text-amber-500', bgColor: 'bg-amber-50', impact: 'Engine stress level - higher = louder operation' },
  'absolute load': { icon: Activity, color: 'text-amber-500', bgColor: 'bg-amber-50', impact: 'Engine stress level - higher = louder operation' },
  
  // Throttle Position
  throttle: { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  tp: { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  tps: { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  'throttle pos': { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  'throttle position': { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  'absolute throttle': { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  'relative throttle': { icon: Wind, color: 'text-emerald-500', bgColor: 'bg-emerald-50', impact: 'Throttle position - affects air intake noise' },
  
  // MAF - Mass Air Flow
  maf: { icon: Wind, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Air flow rate - lean/rich mixture affects sound' },
  'mass air flow': { icon: Wind, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Air flow rate - lean/rich mixture affects sound' },
  'air flow': { icon: Wind, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Air flow rate - lean/rich mixture affects sound' },
  
  // Intake Air Temperature
  iat: { icon: Thermometer, color: 'text-orange-500', bgColor: 'bg-orange-50', impact: 'Intake air temp - affects combustion efficiency' },
  'intake air': { icon: Thermometer, color: 'text-orange-500', bgColor: 'bg-orange-50', impact: 'Intake air temp - affects combustion efficiency' },
  'intake temp': { icon: Thermometer, color: 'text-orange-500', bgColor: 'bg-orange-50', impact: 'Intake air temp - affects combustion efficiency' },
  'intake air temp': { icon: Thermometer, color: 'text-orange-500', bgColor: 'bg-orange-50', impact: 'Intake air temp - affects combustion efficiency' },
  
  // Fuel
  fuel: { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel system - affects engine performance' },
  fli: { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel level - low fuel can cause pump noise' },
  'fuel level': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel level - low fuel can cause pump noise' },
  'fuel pressure': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel pressure - affects injection & combustion' },
  'fuel rail': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel rail pressure - affects injection timing' },
  'fuel trim': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Fuel trim - air/fuel ratio adjustment' },
  'short term fuel': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Short-term fuel trim - real-time adjustment' },
  'long term fuel': { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Long-term fuel trim - learned adjustment' },
  stft: { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Short-term fuel trim - real-time adjustment' },
  ltft: { icon: Fuel, color: 'text-purple-500', bgColor: 'bg-purple-50', impact: 'Long-term fuel trim - learned adjustment' },
  
  // Oxygen Sensors
  o2: { icon: Waves, color: 'text-teal-500', bgColor: 'bg-teal-50', impact: 'Oxygen sensor - indicates combustion quality' },
  o2b1: { icon: Waves, color: 'text-teal-500', bgColor: 'bg-teal-50', impact: 'Oxygen sensor bank 1 - combustion quality' },
  o2b2: { icon: Waves, color: 'text-teal-500', bgColor: 'bg-teal-50', impact: 'Oxygen sensor bank 2 - combustion quality' },
  oxygen: { icon: Waves, color: 'text-teal-500', bgColor: 'bg-teal-50', impact: 'Oxygen sensor - indicates combustion quality' },
  lambda: { icon: Waves, color: 'text-teal-500', bgColor: 'bg-teal-50', impact: 'Lambda sensor - air/fuel ratio monitoring' },
  
  // Voltage / Battery
  voltage: { icon: Battery, color: 'text-indigo-500', bgColor: 'bg-indigo-50', impact: 'System voltage - affects electrical components' },
  vpwr: { icon: Battery, color: 'text-indigo-500', bgColor: 'bg-indigo-50', impact: 'System voltage - affects electrical components' },
  battery: { icon: Battery, color: 'text-indigo-500', bgColor: 'bg-indigo-50', impact: 'Battery voltage - electrical system health' },
  'control module': { icon: Battery, color: 'text-indigo-500', bgColor: 'bg-indigo-50', impact: 'Control module voltage - ECU power supply' },
  
  // Speed
  speed: { icon: Gauge, color: 'text-slate-500', bgColor: 'bg-slate-50', impact: 'Vehicle speed - stationary for idle analysis' },
  vss: { icon: Gauge, color: 'text-slate-500', bgColor: 'bg-slate-50', impact: 'Vehicle speed - stationary for idle analysis' },
  'vehicle speed': { icon: Gauge, color: 'text-slate-500', bgColor: 'bg-slate-50', impact: 'Vehicle speed - stationary for idle analysis' },
  
  // MAP - Manifold Absolute Pressure
  map: { icon: Activity, color: 'text-cyan-500', bgColor: 'bg-cyan-50', impact: 'Manifold pressure - indicates engine vacuum' },
  'manifold': { icon: Activity, color: 'text-cyan-500', bgColor: 'bg-cyan-50', impact: 'Manifold pressure - indicates engine vacuum' },
  'intake manifold': { icon: Activity, color: 'text-cyan-500', bgColor: 'bg-cyan-50', impact: 'Intake manifold pressure - engine load indicator' },
  
  // Ambient/Outside Temperature
  ambient: { icon: Thermometer, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Outside temperature - affects engine performance' },
  'ambient air': { icon: Thermometer, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Outside temperature - affects engine performance' },
  'outside': { icon: Thermometer, color: 'text-sky-500', bgColor: 'bg-sky-50', impact: 'Outside temperature - affects engine performance' },
  
  // Timing
  timing: { icon: Timer, color: 'text-rose-500', bgColor: 'bg-rose-50', impact: 'Ignition timing - affects engine knock & power' },
  'timing advance': { icon: Timer, color: 'text-rose-500', bgColor: 'bg-rose-50', impact: 'Timing advance - ignition timing adjustment' },
  spark: { icon: Zap, color: 'text-yellow-500', bgColor: 'bg-yellow-50', impact: 'Spark advance - affects combustion timing' },
  
  // Oil
  oil: { icon: Droplets, color: 'text-amber-600', bgColor: 'bg-amber-50', impact: 'Oil system - lubrication & engine protection' },
  'oil temp': { icon: Droplets, color: 'text-amber-600', bgColor: 'bg-amber-50', impact: 'Oil temperature - lubrication effectiveness' },
  'oil pressure': { icon: Droplets, color: 'text-amber-600', bgColor: 'bg-amber-50', impact: 'Oil pressure - engine lubrication health' },
  
  // Catalytic Converter
  catalyst: { icon: Activity, color: 'text-green-600', bgColor: 'bg-green-50', impact: 'Catalyst temp - emission system health' },
  'cat temp': { icon: Activity, color: 'text-green-600', bgColor: 'bg-green-50', impact: 'Catalytic converter temperature' },
  
  // EGR
  egr: { icon: Wind, color: 'text-lime-500', bgColor: 'bg-lime-50', impact: 'EGR system - emission control & combustion' },
  'egr error': { icon: Wind, color: 'text-lime-500', bgColor: 'bg-lime-50', impact: 'EGR commanded vs actual difference' },
  
  // Evap
  evap: { icon: Droplets, color: 'text-violet-500', bgColor: 'bg-violet-50', impact: 'Evaporative emission system' },
  
  // Run Time
  runtime: { icon: Timer, color: 'text-gray-500', bgColor: 'bg-gray-50', impact: 'Engine run time since start' },
  'run time': { icon: Timer, color: 'text-gray-500', bgColor: 'bg-gray-50', impact: 'Engine run time since start' },
  'time since': { icon: Timer, color: 'text-gray-500', bgColor: 'bg-gray-50', impact: 'Time since engine codes cleared' },
  
  // Distance / Odometer
  distance: { icon: Gauge, color: 'text-gray-500', bgColor: 'bg-gray-50', impact: 'Distance traveled - mileage tracking' },
  odometer: { icon: Gauge, color: 'text-gray-500', bgColor: 'bg-gray-50', impact: 'Total vehicle mileage' },
  
  // Barometric Pressure
  baro: { icon: Activity, color: 'text-blue-400', bgColor: 'bg-blue-50', impact: 'Barometric pressure - altitude compensation' },
  barometric: { icon: Activity, color: 'text-blue-400', bgColor: 'bg-blue-50', impact: 'Barometric pressure - altitude compensation' },
};

// Function to find matching sensor info by checking multiple fields
function getSensorInfo(sensor) {
  const searchTerms = [
    sensor.shortName,
    sensor.short_name,
    sensor.name,
    sensor.pid
  ].filter(Boolean).map(s => s.toLowerCase());
  
  // Try exact match first
  for (const term of searchTerms) {
    if (SENSOR_DEFINITIONS[term]) {
      return SENSOR_DEFINITIONS[term];
    }
  }
  
  // Try partial match (check if any definition key is contained in any search term)
  for (const term of searchTerms) {
    for (const [key, info] of Object.entries(SENSOR_DEFINITIONS)) {
      if (term.includes(key) || key.includes(term)) {
        return info;
      }
    }
  }
  
  // Keyword-based fallback matching
  const combinedTerms = searchTerms.join(' ');
  if (combinedTerms.includes('temp')) return { icon: Thermometer, color: 'text-orange-500', bgColor: 'bg-orange-50', impact: 'Temperature sensor - thermal monitoring' };
  if (combinedTerms.includes('press')) return { icon: Activity, color: 'text-cyan-500', bgColor: 'bg-cyan-50', impact: 'Pressure sensor - system monitoring' };
  if (combinedTerms.includes('volt')) return { icon: Battery, color: 'text-indigo-500', bgColor: 'bg-indigo-50', impact: 'Voltage monitoring - electrical health' };
  if (combinedTerms.includes('percent') || combinedTerms.includes('%')) return { icon: Percent, color: 'text-amber-500', bgColor: 'bg-amber-50', impact: 'Percentage-based measurement' };
  
  // Default fallback
  return { icon: Activity, color: 'text-slate-500', bgColor: 'bg-slate-50', impact: 'Engine performance parameter' };
}

function getSystemInfo(code) {
  if (!code) return { name: 'Unknown', icon: Info, color: 'text-slate-500' };
  const prefix3 = code.substring(0, 3);
  const prefix2 = code.substring(0, 2);
  const prefix1 = code.substring(0, 1);
  return DTC_SYSTEM_MAP[prefix3] || DTC_SYSTEM_MAP[prefix2] || DTC_SYSTEM_MAP[prefix1] || { name: 'Powertrain', icon: Cog, color: 'text-slate-500' };
}

function categorizeDTCs(dtcs) {
  const engineCritical = [];
  const transmissionCritical = [];
  const other = [];
  
  dtcs.forEach(dtc => {
    const code = dtc.code || dtc;
    if (CRITICAL_ENGINE_CODES.some(c => code.startsWith(c.substring(0, 4)))) {
      engineCritical.push(dtc);
    } else if (CRITICAL_TRANSMISSION_CODES.some(c => code.startsWith(c.substring(0, 4))) || code.startsWith('P07') || code.startsWith('P08')) {
      transmissionCritical.push(dtc);
    } else {
      other.push(dtc);
    }
  });
  
  return { engineCritical, transmissionCritical, other };
}

// Check sensor status based on value and sensor type
function getSensorStatus(sensor) {
  const name = (sensor.shortName || sensor.name || '').toLowerCase();
  const value = parseFloat(sensor.value);
  
  if (isNaN(value)) return 'normal';
  
  // RPM checks
  if (name.includes('rpm') || name.includes('engine speed')) {
    if (value < 600 || value > 1200) return 'warning';
  }
  // Coolant temp checks (in Celsius)
  if (name.includes('coolant') || name === 'ect') {
    if (value < 70 || value > 105) return 'warning';
  }
  // Engine load checks
  if (name.includes('load')) {
    if (value > 50) return 'warning';
  }
  // Voltage checks
  if (name.includes('volt') || name === 'vpwr') {
    if (value < 12 || value > 15.5) return 'warning';
  }
  // Throttle at idle
  if (name.includes('throttle') || name === 'tp' || name === 'tps') {
    if (value > 25) return 'warning';
  }
  
  return 'normal';
}

// Check if sensor is relevant to engine performance
function isEnginePerformanceSensor(sensor) {
  const name = (sensor.shortName || sensor.name || '').toLowerCase();
  const performanceKeywords = [
    'rpm', 'speed', 'engine', 'coolant', 'ect', 'temp', 'load', 'throttle', 'tp', 'tps',
    'maf', 'map', 'air', 'fuel', 'o2', 'oxygen', 'lambda', 'volt', 'timing', 'spark',
    'oil', 'pressure', 'manifold', 'intake', 'trim', 'stft', 'ltft', 'egr', 'catalyst'
  ];
  return performanceKeywords.some(keyword => name.includes(keyword));
}

export default function OBDInsightCard({ obdData, hasObd }) {
  // If no OBD data attached, show enhancement message
  if (!hasObd || !obdData) {
    return (
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="obd-insight-card">
        <div className="border-b border-slate-200 p-3 bg-slate-50/50 flex items-center gap-2">
          <Cpu size={14} className="text-slate-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">OBD-II Analysis</span>
        </div>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
                <Info size={24} className="text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Enhance Your Analysis</p>
                <p className="text-sm text-slate-400">Add OBD-II data for deeper insights</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-sky-50 to-indigo-50 rounded-sm p-3 border border-sky-100">
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-bold text-sky-700">With OBD-II data, we can:</span>
              </p>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Correlate engine sounds with diagnostic trouble codes (DTCs)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Identify root causes using live sensor data (RPM, coolant temp, load)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Provide 3x more accurate diagnosis by combining sound + OBD</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Detect hidden issues not audible in engine sound alone</span>
                </li>
              </ul>
            </div>
            
            <p className="text-sm text-slate-400 text-center italic">
              Use the MFCWL inspection app to capture diagnostic data
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // OBD data exists - show critical codes and live data
  const storedDTCs = obdData.storedDTCs || [];
  const pendingDTCs = obdData.pendingDTCs || [];
  const permanentDTCs = obdData.permanentDTCs || [];
  const liveData = obdData.liveData || [];
  
  const allDTCs = [...storedDTCs, ...pendingDTCs, ...permanentDTCs];
  const { engineCritical, transmissionCritical } = categorizeDTCs(allDTCs);
  
  const totalCritical = engineCritical.length + transmissionCritical.length;
  const hasCriticalIssues = totalCritical > 0;

  // Filter sensors - prioritize engine performance sensors, show all with valid values
  const validSensors = liveData.filter(s => s.value !== undefined && s.value !== null && s.value !== '');
  
  // Separate performance sensors and others
  const performanceSensors = validSensors.filter(isEnginePerformanceSensor);
  const otherSensors = validSensors.filter(s => !isEnginePerformanceSensor(s));
  
  // Combine: performance first, then others (limit to reasonable display)
  const displaySensors = [...performanceSensors, ...otherSensors].slice(0, 12);

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="obd-insight-card">
      <div className="border-b border-slate-200 p-3 bg-slate-50/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cpu size={14} className="text-slate-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">OBD-II Live Data</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="text-sm bg-blue-100 text-blue-700">
            {validSensors.length} Sensors
          </Badge>
          <Badge className={`text-sm ${hasCriticalIssues ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {allDTCs.length} DTCs
          </Badge>
        </div>
      </div>
      <CardContent className="p-3">
        <div className="space-y-3">
          {/* Critical DTCs Summary - Compact */}
          {hasCriticalIssues && (
            <div className="bg-red-50 border border-red-100 rounded-sm p-2">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle size={12} className="text-red-500" />
                <span className="text-sm font-bold text-red-700">
                  {engineCritical.length > 0 && `Engine: ${engineCritical.length}`}
                  {engineCritical.length > 0 && transmissionCritical.length > 0 && ' | '}
                  {transmissionCritical.length > 0 && `Trans: ${transmissionCritical.length}`}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {[...engineCritical, ...transmissionCritical].slice(0, 4).map((dtc, i) => (
                  <span key={i} className="text-sm font-mono font-bold text-red-600 bg-white px-1.5 py-0.5 rounded border border-red-200">
                    {dtc.code || dtc}
                  </span>
                ))}
                {totalCritical > 4 && (
                  <span className="text-sm text-red-500">+{totalCritical - 4}</span>
                )}
              </div>
            </div>
          )}

          {/* Live Sensor Data Grid - Show ALL engine performance parameters */}
          {displaySensors.length > 0 && (
            <div>
              <p className="text-sm text-slate-400 mb-2 uppercase font-bold tracking-wide">
                Live Engine Data ({performanceSensors.length} performance params)
              </p>
              <div className="grid grid-cols-2 gap-2">
                {displaySensors.map((sensor, i) => {
                  // Get display name - prefer shortName, fallback to name
                  const displayName = sensor.shortName || sensor.short_name || sensor.name || 'Unknown';
                  const info = getSensorInfo(sensor);
                  const SensorIcon = info.icon;
                  const status = getSensorStatus(sensor);
                  const displayValue = sensor.displayValue || sensor.display_value || sensor.value;
                  const unit = sensor.unit || '';
                  
                  return (
                    <div 
                      key={i} 
                      className={`${info.bgColor} rounded-sm p-2 border ${status === 'warning' ? 'border-amber-300' : 'border-transparent'}`}
                      data-testid={`sensor-${displayName.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          <SensorIcon size={12} className={`${info.color} flex-shrink-0`} />
                          <span className="text-sm font-bold text-slate-600 truncate" title={sensor.name || displayName}>
                            {displayName}
                          </span>
                        </div>
                        {status === 'warning' && (
                          <AlertTriangle size={10} className="text-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-lg font-bold text-slate-800 leading-none">
                        {displayValue}
                        <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span>
                      </p>
                      {info.impact && (
                        <p className="text-sm text-slate-400 mt-1 leading-tight truncate" title={info.impact}>
                          {info.impact.length > 40 ? info.impact.substring(0, 37) + '...' : info.impact}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {validSensors.length > 12 && (
                <p className="text-sm text-slate-400 mt-2 text-center">
                  +{validSensors.length - 12} more sensors available
                </p>
              )}
            </div>
          )}

          {/* No sensors case */}
          {displaySensors.length === 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-sm p-3 text-center">
              <Info size={16} className="text-amber-500 mx-auto mb-1" />
              <p className="text-sm text-amber-700">No live sensor data available in this OBD scan</p>
            </div>
          )}

          {/* How sensors affect engine sound */}
          <div className="bg-slate-50 rounded-sm p-2 border border-slate-100">
            <p className="text-sm text-slate-500">
              <span className="font-bold">Impact:</span> These readings are correlated with engine sound to identify issues like misfires, lean mixture, or overheating that cause abnormal noise.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
